import React from "react";
import { Eye, Heart, Message, Play, Profile2User, Share, VideoPlay } from "iconsax-reactjs";
import { formatCount, formatReelDate } from "../../utils/reelsShared";
import "./reels.css";

const STATUS_CHIPS = {
  active: { label: "Live", className: "chip chip-success chip-dot" },
  pending: { label: "Processing", className: "chip chip-warning chip-dot" },
};
const HIDDEN_CHIP = { label: "Hidden", className: "chip chip-dot" };

/**
 * One reel in a grid. The video is the content, so the card leads with the
 * cover at reel aspect ratio and keeps the details below it, the same shape
 * the app uses in My Reels.
 *
 * Actions are passed in rather than baked in: the admin console and the dealer
 * portal grant different ones.
 */
export default function ReelCard({ reel, dealerName, propertyTitle, onPreview, actions }) {
  const isHidden = reel.status !== "active";
  const status = STATUS_CHIPS[reel.status] || HIDDEN_CHIP;
  const location = [reel.locality, reel.city].filter(Boolean).join(", ") || "Location not set";

  const counts = [
    { key: "views", label: "Views", icon: Eye, value: reel.views },
    { key: "saves", label: "Saves", icon: Heart, value: reel.saves },
    { key: "comments", label: "Comments", icon: Message, value: reel.commentsCount },
    { key: "enquiries", label: "Enquiries", icon: Profile2User, value: reel.enquiries },
    { key: "shares", label: "Shares", icon: Share, value: reel.shares },
  ];

  return (
    <article className={`reel-card ${isHidden ? "is-hidden" : ""}`}>
      <button
        type="button"
        className="reel-card-media"
        onClick={() => onPreview?.(reel)}
        aria-label={`Play reel${reel.caption ? `: ${reel.caption}` : ""}`}
      >
        {reel.thumbnailUrl ? (
          <img src={reel.thumbnailUrl} alt="" className="reel-card-thumb" loading="lazy" />
        ) : (
          <span className="reel-card-placeholder" aria-hidden="true">
            <VideoPlay size={32} variant="Linear" />
          </span>
        )}
        <span className="reel-card-play" aria-hidden="true">
          <Play size={14} variant="Bold" />
        </span>
      </button>

      <div className="reel-card-body">
        <div className="reel-card-top">
          <span className={status.className}>{status.label}</span>
          <span className="reel-card-date">{formatReelDate(reel.createdAt) || "Publishing…"}</span>
        </div>

        <p className={`reel-card-caption ${reel.caption ? "" : "is-empty"}`}>
          {reel.caption || "No caption"}
        </p>

        <div className="reel-card-meta">
          {propertyTitle ? (
            <span className="reel-card-line" title={propertyTitle}>{propertyTitle}</span>
          ) : null}
          {dealerName ? (
            <span className="reel-card-line reel-card-dealer" title={dealerName}>{dealerName}</span>
          ) : null}
          <span className="reel-card-line" title={location}>{location}</span>
        </div>

        <ul className="reel-card-counts" aria-label="Reel stats">
          {counts.map(({ key, label, icon: Icon, value }) => (
            <li key={key} title={label}>
              <Icon size={14} variant="Linear" aria-hidden="true" />
              <span className="sr-only">{`${label}: `}</span>
              {formatCount(value)}
            </li>
          ))}
        </ul>
      </div>

      {actions ? <div className="reel-card-actions">{actions}</div> : null}
    </article>
  );
}
